import { IntegrationsMarketplaceGridInstalledView } from '../view/IntegrationsMarketplaceGridInstalledView';
import { templateIntegrationsMarketplaceGridInstalled } from '../view/template';
import { IntegrationsMarketplaceGridInstalledRepository } from '../repository/IntegrationsMarketplaceGridInstalledRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent
} from '../../../framework/BaseWebComponentController';
import { IntegrationsMarketplaceGridInstalledWebComponent } from '..';
import { IntegrationsMarketplaceGridInstalledWebComponentSchema } from '../types';
import {
  CustomTemplateListRequest,
  CustomTemplateListResponse,
  GetItemsResponse
} from "../../../ui/gzCustomTemplateList/types";
import {OnDeleteTag} from "../../../tagsManager/tagsManagerGrid/events/events";
import {translateFn} from "../../../utils/translator.helper";
import {GzDialogWebComponent} from "../../../ui/gzDialog";
import {GzButtonWebComponent} from "../../../ui/gzButton";
import {SaveIntegrationEvents} from "../../saveIntegration/types";
import {CreateRequestPayload} from "../../../utils/request";
import {showToast} from "../../../tagsManager/helpers/commonFunctions";
import {ToastSeverity} from "../../../ui/gzToastNotification/type";
import {FilterEvents} from "../../../grid/types/common";

export class IntegrationsMarketplaceGridInstalledController extends BaseWebComponentController {
  public component: IntegrationsMarketplaceGridInstalledWebComponent;
  public dataRepository: IntegrationsMarketplaceGridInstalledRepository;
  public view: IntegrationsMarketplaceGridInstalledView;
  public viewConstructor: typeof IntegrationsMarketplaceGridInstalledView;
  public dataRepositoryConstructor: typeof IntegrationsMarketplaceGridInstalledRepository;

  constructor(component: IntegrationsMarketplaceGridInstalledWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: IntegrationsMarketplaceGridInstalledView,
      viewTemplate: templateIntegrationsMarketplaceGridInstalled,
      dataRepositoryConstructor: IntegrationsMarketplaceGridInstalledRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$wrapper,
        event: SaveIntegrationEvents.Delete,
        callback: (event) => this.showDeleteIntegrationConfirmationWindow(event),
      },
      {
        element: this.view.dom.$wrapper,
        event: SaveIntegrationEvents.Stop,
        callback: (event) => this.showStopIntegrationWindow(event),
      },
      {
        element: this.view.dom.$wrapper,
        event: SaveIntegrationEvents.Start,
        callback: (event) => this.showStartIntegrationWindow(event),
      },
    ];
  }

  buildView() {
    super.buildView();
  }


  async getItems(request: CustomTemplateListRequest): Promise<GetItemsResponse> {
    return this.dataRepository.getItems(request);
  }

  async reloadGrid(filters: any) {
    this.view.dom.$cardboardInstalledGrid.filters = filters;
    await this.view.dom.$cardboardInstalledGrid.controller.render();
  }

  private showDeleteIntegrationConfirmationWindow(event: any) {
    const integrationId = event.detail.integrationId;
    const bodyContentHtml = `
    <p>
     ${translateFn('This will permanently remove the integration and all its associated settings.')}
    </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
        ${translateFn('Delete')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const windowTitle = translateFn('Delete integration?');

    const dialog = GzDialogWebComponent.computeCustomDialog(
      bodyContentHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmButton = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');
        if (confirmButton) {
          confirmButton.showSpinner = true;
          confirmButton.disabled = true;
        }

        const payload =  { _id: integrationId };
        await this.deleteIntegration(
          payload,
        );
        dialog.close();
        await this.reloadGrids();
      },
      null,
      false
    );
  }

  private reloadGrids() {
    this.view.dom.$wrapper.dispatchEvent(
      new CustomEvent(SaveIntegrationEvents.ReloadGrid, {
        bubbles: true,
        cancelable: true,
        composed: true,
      })
    );
  }

  private showStopIntegrationWindow(event: any) {
    const integrationId = event.detail.integrationId;
    const bodyContentHtml = `
    <p>
     ${translateFn('Stopping this integration will suspend its features until it is manually reactivated.')}
    </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
        ${translateFn('Stop')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const windowTitle = translateFn('Stop the integration?');

    const dialog = GzDialogWebComponent.computeCustomDialog(
      bodyContentHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmButton = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');
        if (confirmButton) {
          confirmButton.showSpinner = true;
          confirmButton.disabled = true;
        }

        const payload =  { integrationId: integrationId };
        await this.disableIntegration(
          payload,
        );
        dialog.close();
        await this.reloadGrid(this.view.dom.$cardboardInstalledGrid.filters);
      },
      null,
      false
    );
  }


  private showStartIntegrationWindow(event: any) {
    const integrationId = event.detail.integrationId;
    const bodyContentHtml = `
    <p>
     ${translateFn('Resuming this integration will restore its features and functionality.')}
    </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='full' color='primary' class='mr-2'>
        ${translateFn('Start')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const windowTitle = translateFn('Start the integration?');

    const dialog = GzDialogWebComponent.computeCustomDialog(
      bodyContentHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmButton = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');
        if (confirmButton) {
          confirmButton.showSpinner = true;
          confirmButton.disabled = true;
        }

        const payload =  { integrationId: integrationId };
        await this.enableIntegration(
          payload,
        );
        dialog.close();
        await this.reloadGrid(this.view.dom.$cardboardInstalledGrid.filters);
      },
      null,
      false
    );
  }

  private async deleteIntegration(payload: any) {
    try {
      await this.dataRepository.deleteIntegration(payload);
      showToast(ToastSeverity.SUCCESS, this.dependencies.translateFn('Integration deleted.'));
    } catch (error) {
      showToast(ToastSeverity.ERROR, this.dependencies.translateFn('Failed to delete integration.'));
    }
  }

  private async disableIntegration(payload: any) {
    try {
      await this.dataRepository.disableIntegration(payload);
      showToast(ToastSeverity.SUCCESS, this.dependencies.translateFn('The integration has been stopped.'));
    } catch (error) {
      showToast(ToastSeverity.ERROR, this.dependencies.translateFn('Failed to stop the integration.'));
    }
  }

  private async enableIntegration(payload: any) {
    try {
      await this.dataRepository.enableIntegration(payload);
      showToast(ToastSeverity.SUCCESS, this.dependencies.translateFn('The integration has started.'));
    } catch (error) {
      showToast(ToastSeverity.ERROR, error?.err?.message || this.dependencies.translateFn('Failed to start integration.'));
    }
  }
}
